import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, BuildingIcon, CheckCircle2Icon, GlobeIcon, MailIcon, MessageCircleIcon, PhoneIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import { Form, FormAnswer, FormContactField } from '../../types';
import { postSubmitConfig, whatsappLink } from '../../utils/postSubmit';
import { AnswerMap, visibleQuestions } from '../../utils/conditional';
import { Button } from '../ui/Button';
import { FormField, FormSelect } from './FormField';
import { QuestionField } from './QuestionField';
export interface DynamicFormResult {
  answers: FormAnswer[];
  contact: Record<string, string>;
}
interface DynamicFormProps {
  form: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  /** En vista previa el formulario no envía datos. */
  preview?: boolean;
  /** Permite ocultar la barra de progreso desde el bloque de la landing. */
  showProgress?: boolean;
  /** Permite ocultar el aviso de privacidad desde el bloque de la landing. */
  hidePrivacyNotice?: boolean;
  className?: string;
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Icono dentro del campo de contacto. Reutiliza la librería del proyecto. */
function contactIcon(field: FormContactField): React.ReactNode {
  if (field.type === 'email') return <MailIcon data-id="element-515" data-mp="2oej4uofwha3b" />;
  if (field.type === 'telefono') return <PhoneIcon data-id="element-516" data-mp="2kftgy1x8ka5f" />;
  if (field.type === 'url') return <GlobeIcon data-id="element-517" data-mp="2kcfi33ns6j2q" />;
  if (field.key === 'company') return <BuildingIcon data-id="element-518" data-mp="2fensigalz91p" />;
  return <UserIcon data-id="element-519" data-mp="2fedy8gij6fq7" />;
}

/** Prefijos internacionales para el campo de teléfono. */
const COUNTRY_CODES = [{
  value: '+34',
  label: '🇪🇸 +34'
}, {
  value: '+52',
  label: '🇲🇽 +52'
}, {
  value: '+54',
  label: '🇦🇷 +54'
}, {
  value: '+56',
  label: '🇨🇱 +56'
}, {
  value: '+57',
  label: '🇨🇴 +57'
}, {
  value: '+51',
  label: '🇵🇪 +51'
}, {
  value: '+1',
  label: '🇺🇸 +1'
}, {
  value: '+351',
  label: '🇵🇹 +351'
}, {
  value: '+33',
  label: '🇫🇷 +33'
}, {
  value: '+39',
  label: '🇮🇹 +39'
}, {
  value: '+44',
  label: '🇬🇧 +44'
}, {
  value: '+49',
  label: '🇩🇪 +49'
}];

/**
 * Formulario dinámico paso a paso.
 * Las preguntas NO están escritas dentro del componente: llegan siempre
 * desde la configuración del formulario (`form.questions`).
 */
export function DynamicForm({
  form,
  onSubmit,
  preview = false,
  showProgress = true,
  hidePrivacyNotice = false,
  className = ''
}: DynamicFormProps) {
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [contact, setContact] = useState<Record<string, string>>({});
  const [dialCode, setDialCode] = useState('+34');
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewDone, setPreviewDone] = useState(false);
  const [done, setDone] = useState<DynamicFormResult | null>(null);
  /** Temporizador del avance automático, para no encadenar dos saltos. */
  const advanceTimer = useRef<number | null>(null);
  // Una pregunta sin enunciado no es una pregunta: no ocupa paso ni pantalla.
  const questions = useMemo(() => visibleQuestions(form.questions, answers).filter(question => String(question.label ?? '').trim() !== ''), [form.questions, answers]);

  // El bloque de contacto puede ir al principio (recomendado) o al final.
  const contactFirst = form.contactPosition !== 'final';
  // Un formulario sin datos de contacto no debe mostrar un paso vacío.
  const hasContact = form.contactFields.length > 0;
  const totalSteps = questions.length + (hasContact ? 1 : 0);
  const contactStepIndex = hasContact ? contactFirst ? 0 : questions.length : -1;
  const isContactStep = step === contactStepIndex;
  const current = hasContact && contactFirst ? questions[step - 1] : questions[step];
  const progress = totalSteps > 0 ? Math.round((step + 1) / totalSteps * 100) : 0;

  /**
   * Cada visita empieza SIEMPRE en el primer paso. Este formulario no guarda
   * el progreso del visitante salvo que se active expresamente
   * `saveProgress` en el editor (desactivado por defecto), así que aquí solo
   * se limpia cualquier resto de una visita anterior.
   */
  useEffect(() => {
    if (form.saveProgress) return;
    try {
      window.sessionStorage.removeItem(`ava_form_progress:${form.id}`);
    } catch {
      /* Modo privado de Safari: sin almacenamiento, nada que limpiar. */
    }
  }, [form.id, form.saveProgress]);

  /**
   * Si la lógica condicional oculta preguntas, el paso actual puede quedar
   * fuera de rango; se ajusta en lugar de mostrar una pantalla vacía o
   * descuadrar el contador «Paso X de Y».
   */
  useEffect(() => {
    setStep(s => Math.min(Math.max(0, s), Math.max(0, totalSteps - 1)));
  }, [totalSteps]);

  /** Cancela un avance automático pendiente. */
  function cancelAutoAdvance() {
    if (advanceTimer.current !== null) {
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  }
  useEffect(() => cancelAutoAdvance, []);
  function setAnswer(id: string, value: string | string[]) {
    setAnswers(prev => ({
      ...prev,
      [id]: value
    }));
    setErrors(prev => ({
      ...prev,
      [id]: ''
    }));
  }
  function validateQuestion(): boolean {
    if (!current || !current.required) return true;
    const value = answers[current.id];
    const empty = value === undefined || (Array.isArray(value) ? value.length === 0 : String(value).trim() === '');
    if (empty) {
      setErrors(prev => ({
        ...prev,
        [current.id]: 'Esta pregunta es obligatoria.'
      }));
      return false;
    }
    if (current.type === 'email' && !EMAIL_RE.test(String(value))) {
      setErrors(prev => ({
        ...prev,
        [current.id]: 'Introduce un email válido.'
      }));
      return false;
    }
    return true;
  }
  function validateContact(): boolean {
    const next: Record<string, string> = {};
    form.contactFields.forEach(field => {
      const value = (contact[field.key] || '').trim();
      if (field.required && !value) next[field.key] = 'Campo obligatorio.';else if (field.type === 'email' && value && !EMAIL_RE.test(value)) next[field.key] = 'Introduce un email válido.';else if (field.type === 'telefono' && value && value.replace(/\D/g, '').length < 6) next[field.key] = 'Introduce un teléfono válido.';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function goNext() {
    if (isContactStep ? !validateContact() : !validateQuestion()) return;
    // Un avance manual anula el automático: si no, se saltaba un paso.
    cancelAutoAdvance();
    setDirection(1);
    setStep(s => Math.min(s + 1, totalSteps - 1));
  }
  function goBack() {
    cancelAutoAdvance();
    setDirection(-1);
    setStep(s => Math.max(0, s - 1));
  }

  /** Avance con Enter en preguntas de una sola línea. */
  function handleKeyDown(event: React.KeyboardEvent<HTMLFormElement>) {
    const target = event.target as HTMLElement;
    if (event.key !== 'Enter' || target.tagName === 'TEXTAREA') return;
    if (!isLastStep) {
      event.preventDefault();
      goNext();
    }
  }
  const isLastStep = step === totalSteps - 1;

  /**
   * Nombre del paso mostrado junto al progreso («1 de 7 | Información de
   * contacto | 14%»). Es editable desde el constructor.
   */
  const stepName = isContactStep ? form.contactTitle ?? 'Empecemos por tu información de contacto' : '';

  /** Aviso de privacidad editable y ocultable desde el editor. */
  const notice = form.privacyNotice ?? {
    enabled: true,
    text: 'Al enviar este formulario aceptas que utilicemos tus datos para responder a tu solicitud.'
  };

  /** Qué ocurre cuando el visitante termina el formulario. */
  const action = postSubmitConfig(form);
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;
    if (isContactStep && !validateContact()) return;
    if (!isContactStep && !validateQuestion()) return;
    if (preview || !onSubmit) {
      // En vista previa no se crea ningún lead, pero se confirma que la
      // validación ha pasado y el formulario está listo para publicarse.
      setPreviewDone(true);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: FormAnswer[] = questions.map(question => ({
        questionId: question.id,
        label: question.label,
        value: answers[question.id] ?? ''
      }));
      const phoneKey = form.contactFields.find(f => f.type === 'telefono')?.key;
      const contactData = phoneKey ? {
        ...contact,
        [phoneKey]: `${dialCode} ${contact[phoneKey] ?? ''}`.trim()
      } : contact;
      const result: DynamicFormResult = {
        answers: payload,
        contact: contactData
      };
      await onSubmit(result);
      // El lead ya está guardado. Si la acción posterior se resuelve en la
      // propia página (mensaje o WhatsApp), se muestra aquí; si la acción es
      // ir a la página de gracias o a una URL, el contenedor ya ha navegado.
      cancelAutoAdvance();
      setDone(result);
      if (action.action === 'whatsapp' && action.whatsappAuto) {
        const url = whatsappLink(action, result.contact, result.answers);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      // Se muestra la causa real para poder diagnosticar el fallo.
      setSubmitError(err instanceof Error && err.message ? err.message : 'No hemos podido enviar tu solicitud. Inténtalo de nuevo en unos segundos.');
      setSubmitting(false);
    }
  }
  /** Pantalla final: mensaje simple o WhatsApp, sin página de gracias. */
  if (done && (action.action === 'mensaje' || action.action === 'whatsapp')) {
    const url = whatsappLink(action, done.contact, done.answers);
    return <div className={`mx-auto w-full max-w-[820px] rounded-2xl border border-border bg-background px-6 py-12 text-center shadow-soft sm:rounded-3xl sm:px-10 sm:py-16 ${className}`} role="status" data-id="element-520" data-mp="1alu7qubmp521">
        <CheckCircle2Icon className="mx-auto h-12 w-12 text-green-600" aria-hidden="true" data-id="element-521" data-mp="1kcowq6g9eir6" />
        <h3 className="mt-5 text-[22px] font-semibold tracking-tight text-navy sm:text-[28px]" data-id="element-522" data-mp="1fdcczukoprst">
          {action.messageTitle || form.successMessage}
        </h3>
        <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground sm:text-base" data-id="element-523" data-mp="rpm05jv58i3k">
          {action.messageText}
        </p>
        {action.action === 'whatsapp' && url && <a href={url} target="_blank" rel="noopener noreferrer" className="ava-glow mt-8 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-primary px-7 text-base font-medium text-white transition-colors hover:bg-primary/90" data-id="element-524" data-mp="10ozaeer3dapc">
            <MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-525" data-mp="281rmy2oyljxa" />
            {action.whatsappLabel || 'Hablar por WhatsApp'}
          </a>}
      </div>;
  }
  if (form.questions.length === 0 && form.contactFields.length === 0) {
    return <div className="rounded-2xl border border-dashed border-border bg-surface p-10 text-center text-muted-foreground" data-id="element-526" data-mp="2kletgixj385l">
        Este formulario todavía no tiene preguntas.
      </div>;
  }
  /* `text-left` es deliberado: el bloque de la landing puede centrar toda su
     sección (`textAlign` del estilo), y esa alineación se heredaba dentro de
     la tarjeta dejando las etiquetas centradas. El formulario define su
     propia alineación y solo centra lo que debe ir centrado. */
  return <div className={`ava-form mx-auto w-full max-w-[820px] overflow-hidden rounded-2xl border border-border bg-background text-left shadow-soft sm:rounded-3xl ${className}`} data-id="element-527" data-mp="2okxfji135pa0">
      <div className="px-4 py-6 sm:px-10 sm:py-10" data-id="element-528" data-mp="3haw8hoiei6jy">
        {/* Progreso: compacto en móvil, sin perder legibilidad. */}
        <div className={showProgress ? 'mb-5 sm:mb-8' : 'hidden'} data-id="element-529" data-mp="3m2eb0h02of71">
          <div className="mb-2 flex items-center gap-2 text-[12px] text-muted-foreground sm:mb-2.5 sm:text-[13px]" data-id="element-530" data-mp="3vxcwa1s9nkgq">
            <span className="flex-none font-medium tabular-nums" data-id="element-531" data-mp="3qxqj1scfu47x">
              {step + 1} de {totalSteps}
            </span>
            {stepName && <>
                <span aria-hidden="true" className="flex-none text-border" data-id="element-532" data-mp="3myre47qpi7gh">
                  |
                </span>
                <span className="min-w-0 truncate font-medium text-foreground" data-id="element-533" data-mp="3cyh0wn4o901b">
                  {stepName}
                </span>
              </>}
            <span className="ml-auto flex-none tabular-nums" data-id="element-534" data-mp="386z0c06k91gv">{progress}%</span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Progreso del formulario" data-id="element-535" data-mp="339p9qh7fqjrg">
            <motion.div className="h-full rounded-full bg-primary" initial={false} animate={{
            width: `${Math.max(progress, 4)}%`
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} data-id="element-536" data-mp="2u6xyoiih1q0c" />
          </div>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} noValidate data-id="element-537" data-mp="19posizxjywj0">
          <AnimatePresence mode="wait" initial={false} data-id="element-538" data-mp="19rrtgf08ua2i">
            <motion.div key={isContactStep ? 'contacto' : current?.id} initial={{
            opacity: 0,
            x: direction * 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -20
          }} transition={{
            duration: 0.22,
            ease: 'easeOut'
          }} data-id="element-539" data-mp="1eqcu2fjeqvrh">
              {isContactStep ? <div data-id="element-540" data-mp="uzzn2sifg6cx">
                  {/* El nombre del paso ya aparece junto al progreso: aquí van
                      directamente los campos, sin jerarquías repetidas. */}
                  <div className="grid gap-3.5 sm:grid-cols-2 sm:gap-4" data-id="element-541" data-mp="3aef5v1jtfic">
                    {form.contactFields.map(field => field.type === 'telefono' ? <div key={field.key} className="sm:col-span-2" data-id="element-542" data-mp="79wy4l9t7gij">
                          {/* País ~34 % · Teléfono ~66 %, con la misma altura y
                              separación en cualquier ancho. */}
                          <div className="grid grid-cols-[34%_minmax(0,1fr)] items-start gap-2.5 sm:grid-cols-[minmax(0,150px)_minmax(0,1fr)] sm:gap-3" data-id="element-543" data-mp="3fb1mqi4995ra">
                            <FormSelect id="contact-dial" label="País" options={COUNTRY_CODES} value={dialCode} onChange={e => setDialCode(e.target.value)} data-id="element-544" data-mp="3pb26g561dnw0" />
                            <FormField id={`contact-${field.key}`} label={field.label} required={field.required} type="tel" inputMode="tel" icon={<PhoneIcon data-id="element-546" data-mp="gv8qpsjj4gg0" />} placeholder={field.placeholder} value={contact[field.key] || ''} error={errors[field.key]} onChange={e => setContact(prev => ({
                      ...prev,
                      [field.key]: e.target.value
                    }))} data-id="element-545" data-mp="6z5sgxrst1yc" />
                          </div>
                        </div> : <div key={field.key} className="sm:col-span-2" data-id="element-547" data-mp="2doke413pyx2p">
                          <FormField id={`contact-${field.key}`} label={field.label} required={field.required} type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'} inputMode={field.type === 'email' ? 'email' : undefined} icon={contactIcon(field)} placeholder={field.placeholder} value={contact[field.key] || ''} error={errors[field.key]} onChange={e => setContact(prev => ({
                    ...prev,
                    [field.key]: e.target.value
                  }))} data-id="element-548" data-mp="t5rnefq436ds" />
                        </div>)}
                  </div>
                </div> : current ? <QuestionField question={current} value={answers[current.id]} error={errors[current.id]} onChange={value => {
              setAnswer(current.id, value);
              // Avance automático en selección única. Se cancela el anterior y
              // solo salta si el visitante sigue en este mismo paso, para que
              // un doble toque en el móvil no se salte una pregunta.
              if (current.type === 'seleccion_unica' && !isLastStep) {
                cancelAutoAdvance();
                const from = step;
                advanceTimer.current = window.setTimeout(() => {
                  advanceTimer.current = null;
                  setDirection(1);
                  setStep(s => s === from ? Math.min(s + 1, totalSteps - 1) : s);
                }, 260);
              }
            }} data-id="element-549" data-mp="ei6komx5f96e" /> : null}
            </motion.div>
          </AnimatePresence>

          {submitError && <p role="alert" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-id="element-550" data-mp="2h5loth7ylwan">
              {submitError}
            </p>}

          {previewDone && <p role="status" className="mt-6 rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700" data-id="element-551" data-mp="27cemy6cfgyfb">
              Vista previa: el formulario es válido. En la página publicada este
              envío crearía un contacto en tu CRM.
            </p>}

          <div className="mt-7 flex flex-col-reverse gap-2.5 border-t border-border pt-5 sm:mt-9 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:pt-6" data-id="element-552" data-mp="2v3k18h2pzini">
            {/* En el primer paso no se muestra «Anterior». */}
            {step > 0 ? <Button type="button" variant="ghost" onClick={goBack} disabled={submitting} className="min-h-[48px] w-full text-muted-foreground sm:w-auto" leftIcon={<ArrowLeftIcon className="h-4 w-4" data-id="element-554" data-mp="3hbpdzu2c866u" />} data-id="element-553" data-mp="37eb46dax6fni">
                {form.backLabel || 'Anterior'}
              </Button> : <span aria-hidden="true" className="hidden sm:block" data-id="element-555" data-mp="3mbbtyb9jw0jc" />}
            {isLastStep ? <Button type="submit" size="lg" loading={submitting} disabled={submitting} className="ava-glow min-h-[52px] w-full sm:w-auto" data-id="element-556" data-mp="3qcducvf7eimw">
                {preview ? 'Vista previa' : form.submitLabel}
              </Button> : <Button type="button" size="lg" onClick={goNext} className="min-h-[52px] w-full sm:w-auto" rightIcon={<ArrowRightIcon className="h-4 w-4" data-id="element-558" data-mp="8wfix5wv818k" />} data-id="element-557" data-mp="3xcd3j929qzj">
                {form.nextLabel || 'Siguiente'}
              </Button>}
          </div>
        </form>

        {/* Aviso informativo: nunca bloquea el envío. */}
        {notice.enabled && !hidePrivacyNotice && <p className="mt-5 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-[11px] leading-relaxed text-muted-foreground sm:mt-6 sm:text-xs" data-id="element-559" data-mp="z448uzx9scxk">
            <ShieldCheckIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-560" data-mp="185c4tbcbd1oh" />
            <span data-id="element-561" data-mp="137amwtqmj8el">{notice.text}</span>
            {notice.linkUrl && <a href={notice.linkUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline-offset-2 hover:underline" data-id="element-562" data-mp="135pr70skpjf5">
                {notice.linkLabel || 'Política de privacidad'}
              </a>}
          </p>}
      </div>
    </div>;
}